/**
 * A published campaign's results over its life — slice 44's per-surface
 * panels on the draft's page, between its controls and the folded draft.
 *
 * ## LIFETIME, BECAUSE THAT IS WHAT META IS ASKED FOR
 *
 * The read is `date_preset=maximum`: everything the campaign has ever done,
 * including anything it did before Wend's first start — a campaign switched
 * on in Ads Manager had already spent by then. So the tag says "Lifetime",
 * never "since start", and its spend is the same figure the reading above
 * calls "Spent".
 *
 * ## ONCE META SAYS IT HAS DELIVERED — NOT ONCE WEND SAYS IT STARTED IT
 *
 * This is slice 52, gap 3. Until it, the gate was `record.startedAt !== null`:
 * Wend's record of its own first confirmed start. That is right for a campaign
 * Wend published and nobody has touched — before the first start it has run
 * nowhere, and a section of zeros under it would be noise — and wrong for the
 * one campaign the old comment here named and shrugged at: one switched on in
 * Ads Manager and never started from Wend. It has spend, reach and clicks per
 * surface, Meta will report all of them, and Wend drew none of it because a
 * column in its own database was null.
 *
 * The gate is now `metaHasDelivered(delivery)` — Meta's campaign running, or a
 * spend above zero — read from the delivery the page already holds. Results
 * appear because META says the campaign has delivered, never because Wend's
 * record says Wend started it.
 *
 * **No read writes to the row.** `started_at` stays the record of Wend's own
 * first confirmed start, because it is what decides whether a start switches
 * every ad on; letting a reading write it would let what Meta says change what
 * a start does. Nothing here adopts the campaign, and the drafts panel's badge
 * stays Wend's record, as slices 42 and 44 both settled.
 *
 * ## "WE HAVE NOT BEEN TOLD" AND "NOTHING HAPPENED" ARE DRAWN APART
 *
 * Three answers, three drawings, and the distinction is the whole point of
 * reading a campaign somebody else has been running:
 *
 *   - **rows** → the panels: the whole first (Meta's own reach, overlap
 *     removed), then a panel per placement. A zero Meta measured is drawn `0`.
 *     That is "nothing happened".
 *   - **no rows at all** → one row: the "Lifetime" tag and one "Nothing
 *     reported" chip. That is "we have not been told". Not four "Not measured"
 *     chips in a grid, which is four chips for one fact and reads as four
 *     separate failures. `metaResultsEmpty` draws the line, from the read's
 *     nulls and never from its zeros.
 *   - **the read failed** → the "Results not read" chip with its retry, never
 *     the figures from before it. Unchanged, except that a refusal carrying a
 *     wait now disables the retry until the wait runs out.
 *
 * ## PROVENANCE IS ITS OWN FACT, AND ITS OWN CHIP
 *
 * "Not started from Wend" sits in the tag row beside "Lifetime", drawn
 * whenever `startedAt` is null and there is a delivery to draw from. It says
 * only what Wend knows — that it never sent a start — and not "started in Ads
 * Manager", which Wend cannot see. Neutral, not amber: a campaign somebody
 * started elsewhere is not a fault.
 *
 * ## ONE OUTAGE, ONE ALERT — AND ONE READ TAG FOR THE PAGE
 *
 * The controls above read Meta too, and draw its failure — or the
 * connection's state — as the page's one alert. This section does not add a
 * second: a read that failed is a "Not read" chip with a retry, the history's
 * pattern, and the states decided from the rows (not connected, not checked,
 * not permitted, another account) draw nothing here at all. A re-read that
 * failed is "Not read" too, never the figures from before it; while one is in
 * flight the last figures stay, as the reading's above do.
 *
 * Refresh above re-reads this as well (`reloadKey`), so the reading's own
 * "Read …" tag is the page's one read time and this tag does not print a
 * second. The whole's spend is left to that reading, which prints it as
 * "Spent" — see `MetaAdSurfacePanels`' `wholeSpend`.
 */
import { Badge, Button, Kicker, Spinner, StateChip } from '@wend/ui';

import { metaHasDelivered, type MetaAdDelivery, type MetaAdDraftRecord } from '@wend/shared';

import { fetchMetaAdResults } from '../../lib/api/meta-ads';
import { RetryWait, useRetryWait } from '../../lib/retry-wait';
import { useAsyncData } from '../../lib/use-async-data';
import { metaResultsEmpty } from './meta-ad-controls';
import { MetaAdSurfacePanels } from './MetaAdSurfaces';

export function MetaAdResults({
  record,
  delivery,
  reloadKey,
}: {
  record: MetaAdDraftRecord;
  /**
   * Meta's reading of this campaign, as the controls above made it — `null`
   * until it lands, or when it could not be made. The section waits for it
   * rather than guessing from the record: guessing from the record is exactly
   * the defect this slice closes.
   */
  delivery: MetaAdDelivery | null;
  reloadKey: number;
}) {
  const delivered = record.status === 'published' && delivery !== null && metaHasDelivered(delivery);
  const results = useAsyncData(
    async (signal) => (delivered ? fetchMetaAdResults(record.id, signal) : null),
    [record.id, delivered, record.updatedAt, reloadKey],
  );

  if (!delivered) return null;

  if (results.status === 'error') return <NotRead error={results.error} onRetry={results.reload} />;
  if (results.data === null) {
    return (
      <div data-role="meta-ad-results" data-state="loading" className="flex items-center gap-3 text-[0.9rem] text-muted">
        <Spinner size="sm" label={null} />
      </div>
    );
  }

  /* Wend never sent a start for this campaign. All it can honestly say. */
  const provenance =
    record.startedAt === null ? (
      <Badge tone="neutral" data-role="meta-ad-results-provenance">
        Not started from Wend
      </Badge>
    ) : null;

  const state = results.data;
  switch (state.state) {
    case 'ok':
      /* Meta answered, with nothing in it. One chip for one fact — never a
         grid of "Not measured", and never zeros, which would say the campaign
         ran and did nothing. */
      if (metaResultsEmpty(state.results)) {
        return (
          <section
            data-role="meta-ad-results"
            data-state="nothing_reported"
            aria-label="Lifetime results"
            className="flex flex-wrap items-center gap-2"
          >
            <Kicker as="span">Lifetime</Kicker>
            {state.results.transport === 'stub' && <StateChip state="estimated" label="Simulated" />}
            {provenance}
            <StateChip state="not_measured" label="Nothing reported" />
          </section>
        );
      }
      return (
        <section data-role="meta-ad-results" data-state="ok" aria-label="Lifetime results">
          <MetaAdSurfacePanels figures={state.results} wholeSpend={false} covers="Lifetime" aside={provenance} />
        </section>
      );
    case 'failed':
      return <NotRead error={results.error} onRetry={results.reload} />;
    /* Drawn by the controls above, once. */
    case 'not_published':
    case 'not_connected':
    case 'not_checked':
    case 'not_permitted':
    case 'elsewhere':
      return null;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * The read failed. The chip is the state of the figures; the wait belongs on
 * the control that is disabled, not on the chip — one fault, one chip.
 */
function NotRead({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const wait = useRetryWait(error);
  return (
    <div data-role="meta-ad-results" data-state="failed" className="flex flex-wrap items-center gap-2">
      <StateChip state="not_measured" label="Results not read" />
      <Button type="button" variant="ghost" size="sm" disabled={wait !== null} onClick={onRetry}>
        Try again
      </Button>
      <RetryWait seconds={wait} />
    </div>
  );
}
